
import React, { Component, ErrorInfo, ReactNode } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children?: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  errorId: string;
}

export class ErrorBoundary extends Component<Props, State> {
  private retryCount = 0;
  private maxRetries = 3;

  public state: State = {
    hasError: false,
    errorId: ''
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    console.error(`🚨 [ERROR-BOUNDARY] Error caught (ID: ${errorId}):`, error);
    return { hasError: true, error, errorId };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const errorDetails = {
      errorId: this.state.errorId,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      currentUrl: window.location.href
    };
    
    console.error("🚨 [ERROR-BOUNDARY] Full error details:", errorDetails);
    
    // Log common React errors specifically
    if (error.message.includes('Rendered more hooks')) {
      console.error("🚨 [ERROR-BOUNDARY] Hook rule violation detected - this often causes page refreshes");
    }
    
    this.setState({ errorInfo });
  }

  private handleRetry = () => {
    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      console.log(`🔄 [ERROR-BOUNDARY] Retry attempt ${this.retryCount}/${this.maxRetries}`);
      this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    } else {
      console.warn(`⚠️ [ERROR-BOUNDARY] Max retries (${this.maxRetries}) reached`);
      // FIXED: Don't automatically reload page, let user decide
      console.log(`🔄 [ERROR-BOUNDARY] Consider manual page refresh if issues persist`);
    }
  };

  // FIXED: Add manual refresh handler that doesn't auto-trigger
  private handleManualRefresh = () => {
    console.log(`🔄 [ERROR-BOUNDARY] Manual page refresh requested by user`);
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      // If a custom fallback is provided, use it
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const canRetry = this.retryCount < this.maxRetries;

      // Show our improved error UI
      return (
        <div className="flex flex-col items-center justify-center min-h-[70vh] p-6 space-y-6 text-center">
          <div className="bg-destructive/10 p-3 rounded-full">
            <AlertTriangle className="h-10 w-10 text-destructive" />
          </div>
          <h2 className="text-2xl font-semibold text-mono-seasalt">Something went wrong</h2>
          <div className="max-w-md text-mono-seasalt/80 text-sm">
            <p className="mb-3">There was an error loading your movies. This could be because:</p>
            <ul className="list-disc list-inside text-left space-y-1 mb-4">
              <li>There was a connection issue with our database</li>
              <li>The application encountered an unexpected error</li>
              <li>You're not logged in (viewing demo content)</li>
            </ul>
            <div className="text-xs p-3 bg-mono-outerSpace/30 rounded-md mb-4 overflow-x-auto text-left">
              <p className="font-mono">{this.state.error?.toString()}</p>
              <p className="mt-2 text-mono-seasalt/60">Error ID: {this.state.errorId}</p>
            </div>
          </div>
          <div className="flex gap-4 flex-wrap justify-center">
            {canRetry ? (
              <Button
                onClick={this.handleRetry}
                variant="outline"
                className="bg-mono-outerSpace/30 hover:bg-mono-outerSpace/50 text-mono-seasalt border-mono-slateGray/30"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again ({this.maxRetries - this.retryCount} attempts left)
              </Button>
            ) : (
              <Button
                onClick={this.handleManualRefresh}
                variant="outline"
                className="bg-mono-outerSpace/30 hover:bg-mono-outerSpace/50 text-mono-seasalt border-mono-slateGray/30"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh Page
              </Button>
            )}
            <Link to="/auth">
              <Button variant="monoDark">
                Log In or Sign Up
              </Button>
            </Link>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
